/**
 * Typed literal objects for links (spec §8.11, §7.6). The datatype and language a predicate's shape
 * asks for (`objectDatatype`), the lexical forms XSD accepts, and how a stored value is shown back.
 */
import type { LinkObject } from "./model";

export const XSD = "http://www.w3.org/2001/XMLSchema#";
export const RDF_LANG_STRING = "http://www.w3.org/1999/02/22-rdf-syntax-ns#langString";

export interface DatatypeChoice {
  iri: string;
  /** the datatype's own short name, as `describeLiteral` writes it (`"42"^^decimal`) */
  label: string;
  /** what the reader sees in the menu (spec §8.23); absent for a datatype we do not know */
  labelKey?: string;
  /** an example value; empty means "the generic hint", which the dialog translates */
  placeholder: string;
}

/** The datatypes the Literal tab offers, in the order it offers them. */
export const DATATYPES: DatatypeChoice[] = [
  { iri: "", label: "text", labelKey: "literal.type.text", placeholder: "" },
  { iri: `${XSD}string`, label: "string", labelKey: "literal.type.string", placeholder: "" },
  { iri: `${XSD}integer`, label: "integer", labelKey: "literal.type.integer", placeholder: "42" },
  { iri: `${XSD}decimal`, label: "decimal", labelKey: "literal.type.decimal", placeholder: "42.5" },
  { iri: `${XSD}boolean`, label: "boolean", labelKey: "literal.type.boolean", placeholder: "true" },
  { iri: `${XSD}date`, label: "date", labelKey: "literal.type.date", placeholder: "2026-09-19" },
  { iri: `${XSD}dateTime`, label: "date and time", labelKey: "literal.type.dateTime", placeholder: "2026-09-19T14:30:00Z" },
  { iri: `${XSD}anyURI`, label: "URI", labelKey: "literal.type.uri", placeholder: "https://example.org/" },
  { iri: RDF_LANG_STRING, label: "text with a language", labelKey: "literal.type.langString", placeholder: "" },
];

/** A literal's problem as the catalogue states it (spec §8.23): a key, and the datatype it is about. */
export interface LiteralProblem {
  key: string;
  datatype?: string;
}

const PATTERNS: Record<string, RegExp> = {
  [`${XSD}integer`]: /^[+-]?\d+$/,
  [`${XSD}decimal`]: /^[+-]?(\d+(\.\d*)?|\.\d+)$/,
  [`${XSD}boolean`]: /^(true|false|0|1)$/,
  [`${XSD}date`]: /^-?\d{4}-\d{2}-\d{2}(Z|[+-]\d{2}:\d{2})?$/,
  [`${XSD}dateTime`]: /^-?\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d+)?(Z|[+-]\d{2}:\d{2})?$/,
};

/** One whole sentence per checked datatype: articles and cases differ between languages (§8.23). */
const NOT_A: Record<string, string> = {
  [`${XSD}integer`]: "literal.not.integer",
  [`${XSD}decimal`]: "literal.not.decimal",
  [`${XSD}boolean`]: "literal.not.boolean",
  [`${XSD}date`]: "literal.not.date",
  [`${XSD}dateTime`]: "literal.not.dateTime",
};

/** The datatype a predicate asks for, as one of `DATATYPES`; unknown ones are offered as they are. */
export function choicesFor(objectDatatype: string | null | undefined): DatatypeChoice[] {
  if (!objectDatatype) return DATATYPES;
  const known = DATATYPES.find((d) => d.iri === objectDatatype);
  if (known) return [known, ...DATATYPES.filter((d) => d !== known)];
  const local = objectDatatype.split(/[#/]/).pop() ?? objectDatatype;
  return [{ iri: objectDatatype, label: local, placeholder: "" }, ...DATATYPES];
}

/** Why the value does not fit its datatype, or null when it does. */
export function literalError(value: string, datatype: string, lang: string): LiteralProblem | null {
  if (!value.trim()) return { key: "literal.needValue" };
  if (datatype === RDF_LANG_STRING && !lang.trim()) return { key: "literal.needLang" };
  if (datatype === `${XSD}anyURI` && !/^[a-z][a-z0-9+.-]*:/i.test(value.trim())) return { key: "literal.notUri" };
  const pattern = PATTERNS[datatype];
  if (pattern && !pattern.test(value.trim())) return { key: NOT_A[datatype]!, datatype };
  if (datatype === `${XSD}date` || datatype === `${XSD}dateTime`) {
    if (Number.isNaN(Date.parse(value.trim().replace(/([+-]\d{2}:\d{2}|Z)$/, "") + (datatype === `${XSD}date` ? "T00:00:00Z" : "Z")))) {
      return { key: "literal.notDate" };
    }
  }
  return null;
}

export function literalObject(value: string, datatype: string, lang: string): LinkObject {
  const v = value.trim();
  if (datatype === RDF_LANG_STRING) return { kind: "literal", value: v, lang: lang.trim() };
  return datatype ? { kind: "literal", value: v, datatype } : { kind: "literal", value: v };
}

/** How a stored literal reads in the links panel: `"42"^^decimal`, `"Titration"@de`, `"a note"`. */
export function describeLiteral(o: LinkObject): string {
  if (o.lang) return `“${o.value}”@${o.lang}`;
  if (o.datatype) return `“${o.value}” (${DATATYPES.find((d) => d.iri === o.datatype)?.label ?? o.datatype.split(/[#/]/).pop()})`;
  return `“${o.value}”`;
}
